/**
 * カート状態管理Context
 *
 * @file CartContext.tsx
 * @version 1.0.0
 * @author Japan4U Development Team
 * @summary
 * ショッピングカートのグローバル状態管理を提供。
 * 商品追加、削除、数量変更、合計金額計算等の機能を実装。
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { CartItem, Product } from '@/types';
import { getFirstImageUrl } from '../utils';

interface CartContextType {
  items: CartItem[];
  totalItems: number;
  totalAmount: number;
  addItem: (product: Product, quantity?: number) => void;
  removeItem: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  isInCart: (productId: string) => boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>(() => {
    const saved = localStorage.getItem('cart');
    return saved ? JSON.parse(saved) : [];
  });

  // localStorage同期
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(items));
  }, [items]);

  // 商品追加
  const addItem = useCallback((product: Product, quantity: number = 1) => {
    setItems(prev => {
      const existingItem = prev.find(item => String(item.productId) === String(product.id));

      if (existingItem) {
        return prev.map(item =>
          String(item.productId) === String(product.id)
            ? { ...item, quantity: item.quantity + quantity, subtotal: (item.quantity + quantity) * item.unitPrice }
            : item
        );
      }

      const now = new Date();
      const productName = typeof product.title === 'string' ? product.title : product.title.ja;
      const newItem: CartItem = {
        id: `cart-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        userId: '', // AuthContextから取得する想定
        productId: String(product.id),
        productName,
        sku: product.sku,
        sellerName: String(product.sellerId), // 実際はseller情報から取得
        sellerId: String(product.sellerId),
        quantity,
        unitPrice: product.price,
        subtotal: product.price * quantity,
        currency: product.currency,
        addedAt: now,
        // CartPage互換フィールド
        title: productName,
        price: product.price,
        maxQuantity: product.currentStock,
        image: getFirstImageUrl(product.images),
        stockStatus: product.stockStatus,
        stockCount: product.currentStock,
        variants: undefined,
        createdAt: now,
        updatedAt: now
      };

      return [...prev, newItem];
    });
  }, []);

  // 商品削除
  const removeItem = useCallback((productId: string) => {
    setItems(prev => prev.filter(item => item.productId !== productId));
  }, []);

  // 数量更新
  const updateQuantity = useCallback((productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(productId);
      return;
    }

    setItems(prev =>
      prev.map(item =>
        item.productId === productId
          ? { ...item, quantity, subtotal: item.unitPrice * quantity, updatedAt: new Date() }
          : item
      )
    );
  }, [removeItem]);

  // カートクリア
  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  // カート内チェック
  const isInCart = useCallback((productId: string) => {
    return items.some(item => item.productId === productId);
  }, [items]);

  // 合計アイテム数
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);

  // 合計金額
  const totalAmount = items.reduce((sum, item) => sum + item.subtotal, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        totalItems,
        totalAmount,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        isInCart
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};
